// @givmo/app-admin — the portal layouts' company-less landing (EP-SUITE2-FIX5).
//
// `/` sends every visitor to `/dashboard`, and a signed-in caller with no company used to stay there:
// every company read answers 403, so each section showed "Permission denied" and "Try again" kept
// failing. The portal layouts already read the company (the onboarding trigger's query, the same key),
// so they are where such a caller is recognised — on that read's FIRST 403, not after the app's one
// retry — and REPLACED onto their funnel screen (`companyless-landing.ts`). Replacing matters: the
// portal entry they were bounced off is not one Back should return to. A caller no landing fits stays
// where they were, as before — unless it works for a company on Givmo (a company SSO sign-in's account
// has no sign-in method the table can read): then the request-access screen, which says how to get in
// (D-EP-60, 5; the same rule as the console's). Component-driven on purpose: the layout has rendered
// by then, and a navigation from an effect is an ordinary one — never a redirect thrown at a rendered
// match.

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { companiesGetCompanyOptions } from '@givmo/api-client/query';
import { useAuth } from '../../auth/auth-context';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { useEmployerOfRecord } from '@givmo/employer-module/features/auth/employer-of-record';
import { PORTAL_SIGN_UP_PATHS } from './auth-routing';
import { companylessLanding, isCompanylessRead, landingForEmployee } from './companyless-landing';

export function useCompanylessLanding(): void {
  const { user } = useAuth();
  const navigate = useNavigate();
  // `failureReason` is set by the first failed attempt, while the query is still retrying.
  const { failureReason } = useQuery(companiesGetCompanyOptions());
  const companyless = isCompanylessRead(failureReason);
  const tableLanding =
    companyless && user !== null ? companylessLanding(user, isCompanyJoinOrCreateEnabled()) : null;
  // D-EP-60 (5): asked only for a caller the table cannot place.
  const employer = useEmployerOfRecord(companyless && tableLanding === null ? user : null);
  const landing = landingForEmployee(tableLanding, employer.company, PORTAL_SIGN_UP_PATHS);

  // Once: the landing is recomputed on every render, and the layout leaves with the navigation.
  const landedRef = useRef(false);
  useEffect(() => {
    if (landing === null || landedRef.current) return;
    landedRef.current = true;
    void navigate({ ...landing, replace: true });
  }, [landing, navigate]);
}
